import { useEffect, useState } from "react";
import { api, fmtDuration, getPlaybackUrl } from "@/lib/api";
import { PageHeader, Loading } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Pulse } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Monitoring() {
  const [data, setData] = useState(null);

  const load = () => api.get("/monitoring").then((r) => setData(r.data));
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const openPlayback = async (channelId) => {
    try { const u = await getPlaybackUrl(channelId); window.open(u, "_blank", "noreferrer"); }
    catch { toast.error("Playback URL is issued once the channel is live"); }
  };

  const live = (data || []).filter((c) => c.status === "live").length;
  const warnings = (data || []).filter((c) => c.health === "warning").length;

  return (
    <>
      <PageHeader title="Monitoring" subtitle="Real-time channel health" />
      <div className="p-8 space-y-6 ccs-enter">
        {!data ? <Loading /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <HealthStat label="Total Channels" value={data.length} color="text-white" />
              <HealthStat label="Live" value={live} color="text-emerald-500" />
              <HealthStat label="Warnings" value={warnings} color="text-amber-500" />
              <HealthStat label="Off Air" value={data.filter((c) => c.status === "off_air").length} color="text-red-500" />
            </div>

            <div className="bg-[#141414] border border-[#262626] rounded-sm overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-[#262626]">
                <Pulse size={16} className="text-[#3A86FF]" />
                <h3 className="font-head font-semibold text-sm">Channel Health Monitor</h3>
                <span className="ml-auto font-mono text-[10px] text-[#525252]">AUTO-REFRESH 8s</span>
              </div>
              {data.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#737373]">No channels to monitor.</div>
              ) : (
                <>
                  <div className="grid grid-cols-12 px-5 py-2 border-b border-[#262626] text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold">
                    <div className="col-span-4">Channel · Now / Next</div>
                    <div className="col-span-2">Health</div>
                    <div className="col-span-1">Uptime</div>
                    <div className="col-span-1">Blocks</div>
                    <div className="col-span-2">Runtime</div>
                    <div className="col-span-2">Output</div>
                  </div>
                  {data.map((c) => (
                    <div key={c.id} data-testid={`monitor-row-${c.id}`} className="grid grid-cols-12 items-center px-5 py-3 border-b border-[#1c1c1c] last:border-0 hover:bg-[#1F1F1F] transition-colors duration-150">
                      <div className="col-span-4 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm truncate">{c.name}</span>
                          <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-[#262626] text-[#737373]">{c.mode === "loop" ? "Loop" : "Sched"}</span>
                          {c.brand?.accent_color && <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: c.brand.accent_color }} title="Brand accent" />}
                        </div>
                        {c.now_playing
                          ? <div className="font-mono text-[10px] text-emerald-500 truncate">NOW · {c.now_playing.event.title}</div>
                          : <div className="font-mono text-[10px] text-[#525252]">{c.resolution}</div>}
                        {c.next_asset && <div className="font-mono text-[10px] text-[#525252] truncate">NEXT · {c.next_asset}</div>}
                      </div>
                      <div className="col-span-2"><StatusBadge status={c.health} /></div>
                      <div className="col-span-1 font-mono text-sm tabular-nums">{c.status === "live" ? `${c.uptime}%` : "—"}</div>
                      <div className="col-span-1 font-mono text-sm flex items-center gap-1">
                        {c.scheduled_blocks}
                        {c.conflicts > 0 && <span title={`${c.conflicts} conflicts`} className="h-1.5 w-1.5 rounded-full bg-red-500" />}
                        {c.conflicts === 0 && c.gaps > 0 && <span title={`${c.gaps} gaps`} className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
                      </div>
                      <div className="col-span-2 font-mono text-sm text-[#A3A3A3]">{fmtDuration(c.total_runtime)}</div>
                      <div className="col-span-2">
                        {c.output_status && c.output_status !== "none" ? (
                          <button data-testid={`monitor-manifest-${c.id}`} onClick={() => openPlayback(c.id)}
                            className="inline-flex items-center gap-1.5 text-[11px] font-mono">
                            <span className={`h-1.5 w-1.5 rounded-full ${c.output_status === "live" ? "bg-emerald-500" : "bg-neutral-500"}`} />
                            <span className="text-[#3A86FF] hover:underline">Play</span>
                          </button>
                        ) : <span className="text-[10px] font-mono text-[#525252]">No output</span>}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function HealthStat({ label, value, color }) {
  return (
    <div className="bg-[#141414] border border-[#262626] rounded-sm p-5">
      <div className={`font-head font-black text-4xl tabular-nums ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-1">{label}</div>
    </div>
  );
}
